import React from "react";

export interface SidebarBusinessChildItem {
  id: string;
  isBlocked?: boolean;
  isActive?: boolean;
  label: string;
  onSelect?: () => void;
  to?: string;
}

export interface SidebarBusinessItem {
  children?: readonly SidebarBusinessChildItem[];
  defaultExpanded?: boolean;
  icon?: React.ReactNode;
  id: string;
  isBlocked?: boolean;
  isActive?: boolean;
  label: string;
  onSelect?: () => void;
  to?: string;
}

export interface SidebarBusinessProps {
  ariaLabel?: string;
  className?: string;
  collapseLabel?: string;
  isCollapsed?: boolean;
  isMobileOpen?: boolean;
  items: readonly SidebarBusinessItem[];
  logo?: React.ReactNode;
  onCollapsedChange?: (isCollapsed: boolean) => void;
  onMobileOpenChange?: (isOpen: boolean) => void;
}

export interface UseSidebarBusinessResult {
  collapseSidebar: () => void;
  expandedIds: readonly string[];
  isCollapsedResolved: boolean;
  isItemExpanded: (itemId: string) => boolean;
  isMobileOpenResolved: boolean;
  isSectionActive: (item: SidebarBusinessItem) => boolean;
  selectItem: (item: SidebarBusinessItem | SidebarBusinessChildItem) => void;
  setMobileOpen: (isOpen: boolean) => void;
  toggleItem: (item: SidebarBusinessItem) => void;
  wrapperClassName: string;
}

function shouldExpandItem(item: SidebarBusinessItem): boolean {
  return Boolean(
    item.children?.length && (item.defaultExpanded || item.isActive || item.children.some((child) => child.isActive)),
  );
}

function getExpandedIds(items: readonly SidebarBusinessItem[]): string[] {
  return items.filter(shouldExpandItem).map((item) => item.id);
}

export function useSidebarBusiness({
  className,
  isCollapsed,
  isMobileOpen,
  items,
  onCollapsedChange,
  onMobileOpenChange,
}: SidebarBusinessProps): UseSidebarBusinessResult {
  const [expandedIds, setExpandedIds] = React.useState<readonly string[]>(() => getExpandedIds(items));
  const [internalCollapsed, setInternalCollapsed] = React.useState<boolean>(Boolean(isCollapsed));
  const [internalMobileOpen, setInternalMobileOpen] = React.useState<boolean>(Boolean(isMobileOpen));

  React.useEffect(() => {
    if (typeof isCollapsed === "boolean") {
      setInternalCollapsed(isCollapsed);
    }
  }, [isCollapsed]);

  React.useEffect(() => {
    if (typeof isMobileOpen === "boolean") {
      setInternalMobileOpen(isMobileOpen);
    }
  }, [isMobileOpen]);

  React.useEffect(() => {
    setExpandedIds((currentExpandedIds) => {
      const nextExpandedIds = getExpandedIds(items);

      return Array.from(new Set([...currentExpandedIds, ...nextExpandedIds]));
    });
  }, [items]);

  const isCollapsedResolved = typeof isCollapsed === "boolean" ? isCollapsed : internalCollapsed;
  const isMobileOpenResolved = typeof isMobileOpen === "boolean" ? isMobileOpen : internalMobileOpen;

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isMobileOpenResolved) {
        if (typeof isMobileOpen !== "boolean") {
          setInternalMobileOpen(false);
        }

        onMobileOpenChange?.(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileOpen, isMobileOpenResolved, onMobileOpenChange]);

  const setCollapsed = (nextValue: boolean): void => {
    if (typeof isCollapsed !== "boolean") {
      setInternalCollapsed(nextValue);
    }

    onCollapsedChange?.(nextValue);
  };

  const setMobileOpen = (nextValue: boolean): void => {
    if (typeof isMobileOpen !== "boolean") {
      setInternalMobileOpen(nextValue);
    }

    onMobileOpenChange?.(nextValue);
  };

  const isSectionActive = (item: SidebarBusinessItem): boolean => {
    if (item.isActive) {
      return true;
    }

    return Boolean(item.children?.some((child) => child.isActive));
  };

  const selectItem = (item: SidebarBusinessItem | SidebarBusinessChildItem): void => {
    item.onSelect?.();

    if (isMobileOpenResolved) {
      setMobileOpen(false);
    }
  };

  const toggleItem = (item: SidebarBusinessItem): void => {
    if (!item.children?.length) {
      selectItem(item);
      return;
    }

    if (isCollapsedResolved) {
      setCollapsed(false);
      return;
    }

    setExpandedIds((currentExpandedIds) =>
      currentExpandedIds.includes(item.id)
        ? currentExpandedIds.filter((currentId) => currentId !== item.id)
        : [...currentExpandedIds, item.id],
    );
  };

  const collapseSidebar = (): void => {
    setCollapsed(!isCollapsedResolved);
  };

  const wrapperClassName = [
    "sidebar-business",
    isCollapsedResolved ? "sidebar-business--collapsed" : "",
    isMobileOpenResolved ? "sidebar-business--mobile-open" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return {
    collapseSidebar,
    expandedIds,
    isCollapsedResolved,
    isItemExpanded: (itemId: string) => expandedIds.includes(itemId),
    isMobileOpenResolved,
    isSectionActive,
    selectItem,
    setMobileOpen,
    toggleItem,
    wrapperClassName,
  };
}
